import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { employeesApi, ncbApi, fmtCents, type Employee, type EmployeeInput, type EmployeeBenefitRow, type NcbType } from "../lib/business";

export function EmployeesPage() {
  const [editing, setEditing] = useState<Employee | null>(null);
  const [q, setQ] = useState("");
  const { data, refetch } = useQuery({
    queryKey: ["employees", q],
    queryFn: () => employeesApi.list(q),
  });

  return (
    <>
      <div className="stage-header">
        <div>
          <div className="stage-eyebrow">Employees</div>
          <h1 className="stage-title">Your <em>team</em>.</h1>
          <p className="stage-sub">Salary, bank details, and non-cash benefits feed payroll + APIT.</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <input
              className="form-input"
              style={{ maxWidth: 280 }}
              placeholder="Search by name, emp #, NIC…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <ImportCsvButton onDone={() => refetch()} />
              <button className="btn btn-primary" onClick={() => setEditing({} as Employee)}>+ New employee</button>
            </div>
          </div>

          <div className="card card-pad-lg table-cards table-scroll" style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Emp #</th>
                  <th style={th}>Name</th>
                  <th style={th}>NIC</th>
                  <th style={{ ...th, textAlign: "right" }}>Base salary</th>
                  <th style={th}>EPF #</th>
                  <th style={th}>ETF #</th>
                </tr>
              </thead>
              <tbody>
                {!data?.employees.length && (
                  <tr><td colSpan={6} data-fullwidth style={{ padding: 60, textAlign: "center", color: "var(--ink-3)" }}>No employees yet — click + New employee.</td></tr>
                )}
                {data?.employees.map((e) => (
                  <tr key={e.id} onClick={() => setEditing(e)} style={{ cursor: "pointer" }}>
                    <td style={td} data-label="Emp #">{e.emp_no}</td>
                    <td style={td} data-label="Name"><strong>{e.full_name}</strong></td>
                    <td style={{ ...td, fontSize: 12, color: "var(--ink-3)" }} data-label="NIC">{e.nic ?? "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono, monospace)" }} data-label="Base salary">{fmtCents(e.base_salary_cents)}</td>
                    <td style={{ ...td, fontFamily: "var(--font-mono, monospace)", fontSize: 12 }} data-label="EPF #">{e.epf_member_no ?? "—"}</td>
                    <td style={{ ...td, fontFamily: "var(--font-mono, monospace)", fontSize: 12 }} data-label="ETF #">{e.etf_member_no ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <EmployeeForm editing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />
      </div>
    </>
  );
}

function EmployeeForm({ editing, onClose, onSaved }: {
  editing: Employee | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial: EmployeeInput = editing ? {
    empNo: editing.emp_no ?? "",
    fullName: editing.full_name ?? "",
    nic: editing.nic,
    email: editing.email,
    phone: editing.phone,
    tin: editing.tin,
    epfMemberNo: editing.epf_member_no,
    etfMemberNo: editing.etf_member_no,
    baseSalaryCents: editing.base_salary_cents ?? "0",
    bankName: editing.bank_name,
    bankBranch: editing.bank_branch,
    bankAccountNo: editing.bank_account_no,
    isActive: editing.is_active,
  } : { empNo: "", fullName: "", baseSalaryCents: "0", isActive: true };

  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!editing) {
    return (
      <div className="card card-pad-lg" style={{ background: "var(--paper-2)" }}>
        <div className="stage-eyebrow">New employee</div>
        <p style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 8 }}>
          Click "+ New employee" or pick a row to edit.
        </p>
      </div>
    );
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const body: EmployeeInput = {
        empNo: draft.empNo,
        fullName: draft.fullName,
        nic: draft.nic || null,
        email: draft.email || null,
        phone: draft.phone || null,
        tin: draft.tin || null,
        epfMemberNo: draft.epfMemberNo || null,
        etfMemberNo: draft.etfMemberNo || null,
        baseSalaryCents: draft.baseSalaryCents,
        bankName: draft.bankName || null,
        bankBranch: draft.bankBranch || null,
        bankAccountNo: draft.bankAccountNo || null,
        isActive: draft.isActive,
      };
      if (editing!.id) await employeesApi.update(editing!.id, body);
      else await employeesApi.create(body);
      onSaved();
    } catch {
      setErr("Could not save. Check fields.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card card-pad-lg" style={{ position: "sticky", top: 80 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div className="stage-eyebrow">{editing.id ? "Edit" : "New"}</div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500 }}>
            {editing.id ? editing.full_name : "Add employee"}
          </div>
        </div>
        <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 18 }} onClick={onClose}>×</button>
      </div>

      <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8 }}>
          <div className="form-field">
            <label className="form-label">Emp #</label>
            <input className="form-input" value={draft.empNo} onChange={(e) => setDraft({ ...draft, empNo: e.target.value })} required />
          </div>
          <div className="form-field">
            <label className="form-label">Full name</label>
            <input className="form-input" value={draft.fullName} onChange={(e) => setDraft({ ...draft, fullName: e.target.value })} required />
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">NIC</label>
          <input className="form-input" value={draft.nic ?? ""} onChange={(e) => setDraft({ ...draft, nic: e.target.value })} />
        </div>
        <div className="form-field">
          <label className="form-label">Base salary (cents)</label>
          <input className="form-input num" type="number" value={String(draft.baseSalaryCents ?? "")} onChange={(e) => setDraft({ ...draft, baseSalaryCents: e.target.value })} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div className="form-field">
            <label className="form-label">EPF member #</label>
            <input className="form-input" value={draft.epfMemberNo ?? ""} onChange={(e) => setDraft({ ...draft, epfMemberNo: e.target.value })} />
          </div>
          <div className="form-field">
            <label className="form-label">ETF member #</label>
            <input className="form-input" value={draft.etfMemberNo ?? ""} onChange={(e) => setDraft({ ...draft, etfMemberNo: e.target.value })} />
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">TIN</label>
          <input className="form-input" value={draft.tin ?? ""} onChange={(e) => setDraft({ ...draft, tin: e.target.value })} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div className="form-field">
            <label className="form-label">Bank</label>
            <input className="form-input" value={draft.bankName ?? ""} onChange={(e) => setDraft({ ...draft, bankName: e.target.value })} />
          </div>
          <div className="form-field">
            <label className="form-label">Branch</label>
            <input className="form-input" value={draft.bankBranch ?? ""} onChange={(e) => setDraft({ ...draft, bankBranch: e.target.value })} />
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">Account #</label>
          <input className="form-input" value={draft.bankAccountNo ?? ""} onChange={(e) => setDraft({ ...draft, bankAccountNo: e.target.value })} />
        </div>

        {err && <div className="banner banner-error">{err}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 1, justifyContent: "center" }}>
            {saving ? "Saving…" : editing.id ? "Save" : "Add"}
          </button>
        </div>
      </form>

      {editing.id && <BenefitsPanel employeeId={editing.id} />}
    </div>
  );
}

function BenefitsPanel({ employeeId }: { employeeId: string }) {
  const qc = useQueryClient();
  const { data: empData } = useQuery({
    queryKey: ["employee-detail", employeeId],
    queryFn: () => employeesApi.get(employeeId),
  });
  const { data: typesData } = useQuery({
    queryKey: ["ncb-types"],
    queryFn: () => ncbApi.types(),
  });
  const benefits: EmployeeBenefitRow[] = empData?.benefits ?? [];
  const types: NcbType[] = typesData?.types ?? [];
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState("");
  const [costCents, setCostCents] = useState("");
  const [km, setKm] = useState("");

  async function add() {
    if (!picked) return;
    const body: any = { benefitTypeId: picked };
    if (costCents) body.costCents = costCents;
    if (km) body.kmThisMonth = Number(km);
    await employeesApi.addBenefit(employeeId, body);
    setAdding(false); setPicked(""); setCostCents(""); setKm("");
    qc.invalidateQueries({ queryKey: ["employee-detail", employeeId] });
  }

  async function remove(benefitId: string) {
    await employeesApi.removeBenefit(employeeId, benefitId);
    qc.invalidateQueries({ queryKey: ["employee-detail", employeeId] });
  }

  const pickedType = types.find((t) => t.id === picked);
  const showCost = pickedType?.formula === "PCT_OF_COST" || pickedType?.formula === "COST_OR_MARKET";
  const showKm = pickedType?.formula === "PER_KM";

  return (
    <div style={{ marginTop: 18, paddingTop: 18, borderTop: "0.5px solid var(--line)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="stage-eyebrow">Non-cash benefits</div>
        {!adding && <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setAdding(true)}>+ Add</button>}
      </div>

      {benefits.length === 0 && !adding && (
        <p style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 8 }}>No NCB assigned.</p>
      )}

      {benefits.map((b) => (
        <div key={b.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "0.5px solid var(--line)" }}>
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{b.benefit_type_name}</div>
            <div style={{ fontSize: 11, color: "var(--ink-3)" }}>{b.formula}{b.cost_cents ? ` · cost ${fmtCents(b.cost_cents)}` : ""}</div>
          </div>
          <button type="button" className="btn btn-ghost" style={{ fontSize: 16, padding: "0 6px" }} onClick={() => remove(b.id)}>×</button>
        </div>
      ))}

      {adding && (
        <div style={{ marginTop: 10, padding: 10, background: "var(--paper-2)", borderRadius: 6 }}>
          <div className="form-field">
            <label className="form-label">Benefit type</label>
            <select className="form-input" value={picked} onChange={(e) => setPicked(e.target.value)}>
              <option value="">— Pick a type —</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {showCost && (
            <div className="form-field">
              <label className="form-label">Cost (cents)</label>
              <input className="form-input num" type="number" value={costCents} onChange={(e) => setCostCents(e.target.value)} />
            </div>
          )}
          {showKm && (
            <div className="form-field">
              <label className="form-label">Km this month</label>
              <input className="form-input num" type="number" value={km} onChange={(e) => setKm(e.target.value)} />
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button type="button" className="btn btn-primary" onClick={add} disabled={!picked} style={{ flex: 1 }}>Add</button>
            <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: "left", padding: "14px 18px", fontSize: 11, letterSpacing: "0.1em",
  textTransform: "uppercase", color: "var(--ink-4)", fontWeight: 500,
  background: "var(--paper)", borderBottom: "0.5px solid var(--line)",
};
const td: React.CSSProperties = { padding: "14px 18px", fontSize: 13, borderBottom: "0.5px solid var(--line)" };

function ImportCsvButton({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: number; updated: number; errors: Array<{ row: number; message: string }> } | null>(null);

  async function pick() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,text/csv";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setBusy(true); setResult(null);
      try {
        const csv = await file.text();
        const r = await employeesApi.importCsv(csv);
        setResult(r);
        onDone();
      } catch (e: any) {
        setResult({ created: 0, updated: 0, errors: [{ row: 0, message: e?.message ?? "import failed" }] });
      } finally {
        setBusy(false);
      }
    };
    input.click();
  }

  return (
    <>
      <button className="btn btn-line" onClick={pick} disabled={busy} title="Header row required: emp_no, full_name + optional nic, email, phone, base_salary_cents, epf_member_no, etf_member_no, bank_name, bank_branch, bank_account_no, tin">
        {busy ? "Importing…" : "Import CSV"}
      </button>
      {result && (
        <div
          style={{
            position: "fixed", bottom: 20, right: 20, zIndex: 100,
            background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 8,
            padding: 14, maxWidth: 360, boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ fontWeight: 500, marginBottom: 6 }}>
            ✓ {result.created} created, {result.updated} updated
          </div>
          {result.errors.length > 0 && (
            <details>
              <summary style={{ color: "var(--err, #c00)", fontSize: 13, cursor: "pointer" }}>
                {result.errors.length} error{result.errors.length === 1 ? "" : "s"}
              </summary>
              <ul style={{ marginTop: 6, fontSize: 12, color: "var(--ink-3)", listStyle: "none", paddingLeft: 0 }}>
                {result.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>Row {e.row}: {e.message}</li>
                ))}
                {result.errors.length > 10 && <li>…and {result.errors.length - 10} more</li>}
              </ul>
            </details>
          )}
          <button className="btn btn-ghost" style={{ marginTop: 8, fontSize: 12 }} onClick={() => setResult(null)}>Close</button>
        </div>
      )}
    </>
  );
}
